import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ContentHeader } from "@cx/ui";
import type { AnnualDashboardResult, DashboardResult } from "@cx/contracts";
import { formatDateBR, todayInSaoPaulo } from "@cx/domain";
import { useSession } from "../../session";
import { ChartCard } from "./ChartCard";
import { count, dayMonth, money, periodLabel, qty, reportQuery, shortMonth } from "./labels";
import { statusList } from "./search";
import {
  FilterRow,
  ReportStatus,
  SettingsButton,
  StatusFilter,
  reportQueryOptions,
  useDashboardOptions,
} from "./shared";
import { ChannelTable, DreTable, GoalsCard, ProductTable, SalesKpis } from "./widgets";
import { SmartForecast } from "../agents/SmartForecast";
import { CompanyTabs } from "./CompanyTabs";

const PATH = "/meu-negocio/dashboard";

/**
 * Dashboard Mensal (RF-BI-01..04, 06): KPIs com variação, valor por dia × comparação, DRE resumida, previsão do mês,
 * metas, canais, Top SKUs e produtos pai, e a estatística anual.
 */
export function DashboardPage(): ReactElement {
  const { companyId, api } = useSession();
  const search = useSearch({ from: PATH });
  const navigate = useNavigate({ from: PATH });
  const options = useDashboardOptions();
  const statuses = statusList(search.statuses);
  const set = (patch: Partial<typeof search>) =>
    navigate({ search: (s) => ({ ...s, ...patch }), replace: true });
  // Datas só entram no filtro em pares (o servidor exige os dois ou nenhum).
  const pair = (a?: string, b?: string) => (a && b ? [a, b] : [undefined, undefined]);
  const [from, to] = pair(search.from, search.to);
  const [compareFrom, compareTo] = pair(search.compareFrom, search.compareTo);
  const d = useQuery({
    ...reportQueryOptions,
    queryKey: ["dashboard", companyId, "monthly", from, to, compareFrom, compareTo, search.statuses],
    queryFn: () =>
      api<DashboardResult>(
        `/reports/dashboard${reportQuery({ from, to, compareFrom, compareTo, statuses })}`,
      ),
  });
  const r = d.data;

  return (
    <>
      <ContentHeader
        title="Dashboard"
        breadcrumb={["Meu Negócio", "Dashboard"]}
        actions={<SettingsButton />}
      />
      <CompanyTabs />
      <FilterRow>
        <DateInput
          id="dash-from"
          label="De"
          value={search.from ?? r?.period.from}
          onChange={(v) => set({ from: v })}
        />
        <DateInput
          id="dash-to"
          label="Até"
          value={search.to ?? r?.period.to}
          onChange={(v) => set({ to: v })}
        />
        <DateInput
          id="dash-cfrom"
          label="Comparar de"
          value={search.compareFrom ?? r?.compare.from}
          onChange={(v) => set({ compareFrom: v })}
        />
        <DateInput
          id="dash-cto"
          label="Comparar até"
          value={search.compareTo ?? r?.compare.to}
          onChange={(v) => set({ compareTo: v })}
        />
        <div className="col-12 col-md-3">
          {options.data && (
            <StatusFilter
              statuses={options.data.statuses}
              defaults={options.data.settings.saleStatusIds}
              value={statuses}
              onChange={(ids) => set({ statuses: ids?.join(",") })}
            />
          )}
        </div>
        <div className="col-12 col-md-auto">
          <button
            type="button"
            className="btn btn-link"
            onClick={() => navigate({ search: {}, replace: true })}
          >
            Mês atual
          </button>
        </div>
      </FilterRow>
      <ReportStatus loading={d.isLoading} error={d.error} />
      {r && (
        <>
          <p className="text-muted small">
            Período de {periodLabel(r.period)} comparado com {periodLabel(r.compare)} · Situações:{" "}
            {r.statuses.map((s) => s.label).join(", ")}
          </p>
          <SalesKpis cards={r.cards} />
          {r.forecast && (
            <div className="alert alert-info d-flex flex-wrap gap-2" data-testid="forecast">
              <strong>Previsão de fechamento em {formatDateBR(r.forecast.monthEnd)}:</strong>
              <span>{money(r.forecast.projectedCents)}</span>
              <span className="text-muted">
                (entre {money(r.forecast.lowCents)} e {money(r.forecast.highCents)}, com 80% de confiança)
              </span>
              <SmartForecast />
            </div>
          )}
          <div className="row g-3 mb-3">
            <div className={r.goals ? "col-xl-8" : "col-12"}>
              <ChartCard
                title="Valor total de pedidos por dia"
                categories={r.days.map(dayMonth)}
                series={[
                  { name: `Período (${periodLabel(r.period)})`, data: r.daily.map((p) => p.totalCents) },
                  {
                    name: `Comparação (${periodLabel(r.compare)})`,
                    data: r.compareDaily.map((p) => p.totalCents),
                  },
                ]}
                unit="money"
                filename="valor-por-dia"
                testId="chart-daily"
              />
            </div>
            {r.goals && (
              <div className="col-xl-4">
                <GoalsCard goals={r.goals} />
              </div>
            )}
          </div>
          <div className="row g-3 mb-3">
            <div className="col-xl-6">
              <DreTable dre={r.dre} />
            </div>
            <div className="col-xl-6">
              <ChannelTable rows={r.channels} />
            </div>
          </div>
          {r.channelDaily.length > 1 && (
            <div className="mb-3">
              <ChartCard
                title="Valor por canal por dia"
                type="bar"
                stacked
                categories={r.days.map(dayMonth)}
                series={r.channelDaily.map((c) => ({ name: c.label, data: c.values }))}
                unit="money"
                filename="valor-por-canal"
              />
            </div>
          )}
          <div className="row g-3 mb-3">
            <div className="col-xl-6">
              <ProductTable
                title="Produtos mais vendidos (Top SKUs)"
                rows={r.topProducts}
                testId="top-products"
              />
            </div>
            <div className="col-xl-6">
              <ProductTable
                title="Produtos pai (variações somadas)"
                rows={r.topParents}
                testId="top-parents"
              />
            </div>
          </div>
        </>
      )}
      <AnnualSection statuses={statuses} />
    </>
  );
}

export function DateInput({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string | undefined;
  onChange(v: string | undefined): void;
}) {
  return (
    <div className="col-6 col-md-2">
      <label className="form-label small" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="date"
        className="form-control"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || undefined)}
      />
    </div>
  );
}

/** Estatística anual (RF-BI-04): mês a mês do ano × ano anterior. */
function AnnualSection({ statuses }: { statuses: string[] | undefined }): ReactElement {
  const { companyId, api } = useSession();
  const [year, setYear] = useState(() => Number(todayInSaoPaulo().slice(0, 4)));
  const a = useQuery({
    ...reportQueryOptions,
    queryKey: ["dashboard", companyId, "annual", year, statuses?.join(",")],
    queryFn: () => api<AnnualDashboardResult>(`/reports/annual${reportQuery({ year, statuses })}`),
  });
  const r = a.data;
  return (
    <div className="mb-3">
      <ReportStatus loading={a.isLoading} error={a.error} />
      {r && (
        <ChartCard
          title={`Estatística anual: ${r.year} × ${r.year - 1}`}
          type="bar"
          categories={r.months.map((m) => shortMonth(m.month))}
          series={[
            { name: String(r.year), data: r.months.map((m) => m.current.totalCents) },
            { name: String(r.year - 1), data: r.months.map((m) => m.previous.totalCents) },
          ]}
          unit="money"
          filename={`estatistica-anual-${r.year}`}
          testId="chart-annual"
          actions={
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="dash-year" className="small text-muted">
                Ano
              </label>
              <input
                id="dash-year"
                type="number"
                className="form-control form-control-sm"
                style={{ width: "6rem" }}
                min={1901}
                max={2199}
                value={year}
                onChange={(e) => {
                  const y = Number(e.target.value);
                  if (Number.isInteger(y) && y >= 1901 && y <= 2199) setYear(y);
                }}
              />
            </div>
          }
        />
      )}
      {r && (
        <p className="small text-muted mt-2 mb-0" data-testid="annual-totals">
          Total {r.year}: {money(r.totals.current.totalCents)} em {count(r.totals.current.orders)} pedido(s) e{" "}
          {qty(r.totals.current.itemsQ4)} item(ns) · {r.year - 1}: {money(r.totals.previous.totalCents)}
        </p>
      )}
    </div>
  );
}
